// The client's `rounds-trace` decoder: a pure, DOM-free mirror of
// `waggle-round/src/trace/format.rs` (trace format version 5, the last this change moves the
// format to). It reads the three published components of a batch run's trace the review
// screen needs — the coarse tier (downloaded whole), the detail tier (length-framed, one
// record per tick) and the fixed-width tick→offset index over it — so the review screen can
// reach ANY tick as an O(1) range read (plan A4) instead of re-simulating to get there.
//
// A version-5 trace declares a **fourth** component, `balance.toml`: the resolved
// `Balance` the run executed (`rounds-balance` design **E10**). It is deliberately not
// decoded here. It exists so a *re-simulation* can reconstruct the run's world, which is
// a server-side concern — the client renders a written trace and never builds a world —
// and decoding it would mean shipping a TOML parser and a mirror of the whole balance
// schema for no reader on this side. The declaration is simply not consulted.
//
// The coarse tier's **first** record is a preamble carrying the flower-patch table — id
// and quantized position, fixed for the whole run — and every later record is a sample.
// The per-tick detail patch section is positional against that table rather than
// self-describing, so decoding a detail record takes the preamble as well as the
// record's own bytes. Every caller that reaches a detail record therefore reaches the
// preamble first, on the same coarse-tier read it already had to do — there is no second
// stream to fetch, order, or lose track of.
//
// From format version 4 a detail record is a **keyframe or a delta**: patches, spills, cells
// and (from version 5) bees are delta-encoded against the tick immediately before them,
// behind keyframes at the manifest's declared cadence — only the per-colony economy section
// is still written whole on every delta record. A lone record is therefore no longer
// decodable on its own — the decodable unit is a **keyframe-anchored span** — so this module
// carries a `SpanReplayer` mirroring `format.rs`'s type of the same name, and every reader of
// the detail tier (the published range fetch, the in-flight prefix, and `openTrace`'s own
// per-tick access) feeds it records in order rather than decoding one payload in isolation.
//

// Written in the shape `tools/snapshot-player/decode.js` established (the
// `snapshot-player` capability already calls that module "the template for the Stage-3
// client decoder"): no DOM API, every read bounds-checked, and structural failure
// raised as a typed error rather than silently producing `NaN`/`undefined`.
//
// **Fail-closed, deliberately** (`rounds-trace` D4). This slice evicts nothing, so a
// manifest-declared component whose bytes are absent, short, over-long, mis-counted or
// whose index points outside `detail.bin` is CORRUPTION — a `TraceCorruptionError`,
// never a partial or "just show coarse" view. The review screen surfaces that as a real
// failure precisely because absent declared bytes are never intentional here.
//
// The per-component BLAKE3 checksums the manifest carries are verified server-side (the
// Rust reader's fail-closed path); the client has no BLAKE3 and deliberately does not
// pull one in — it validates the structural declarations it CAN check and otherwise
// trusts the corruption error the fetch reports.

/** The trace format version this decoder reads. A different version is refused
 * outright — this slice fails closed rather than negotiating (`rounds-trace-http`
 * will negotiate). Mirrors `format::TRACE_FORMAT_VERSION`.
 *
 * **1 → 2**: the resolved balance became a required fourth component.
 *
 * **2 → 3** (`rounds-trace-size` stage A): the detail tier stops re-writing match-static
 * geometry every tick. The flower-patch table moves into a single preamble record at the
 * head of the coarse tier, and the per-tick patch section becomes a bare fixed-order
 * `(pool, dead)` array indexed against it. The coarse tier gains a record-kind byte so a
 * preamble can never be decoded as a sample, every detail record gains a leading
 * per-section keyframe mask (every bit set at this version — every v3 record is still
 * self-contained), and the bee entry narrows to 15 B by packing `gathering` and the
 * activity class into one `flags` byte.
 *
 * **3 → 4** (`rounds-trace-size` stage B): patches, spills and cells stop being re-written
 * every tick. A detail record is now a **keyframe** (mask `0b11111`, every section whole —
 * the only shape v3 ever wrote) or a **delta** (mask `0b10001` at this version, `SECTION_BEES
 * | SECTION_COLONIES` — those two sections stay whole every tick; everything else is
 * delta-encoded against the tick immediately before it, named in a
 * `base_tick` the reader **verifies** rather than trusts). The decodable unit becomes a
 * **keyframe-anchored span**: a keyframe followed by the deltas that depend on it, replayed
 * in order by a `SpanReplayer`. Keyframes fall at `first_tick + n·keyframe_cadence_ticks`,
 * declared in the manifest so a measurement can move the cadence without another format
 * move. Tick contiguity becomes required within a trace's written range: under a delta
 * format one silently missing tick would corrupt every following tick while every other
 * validation still passed.
 *
 * **4 → 5** (`rounds-trace-size` stage C, the last format move this change makes): bees join
 * the delta scheme too, so the delta mask narrows to `0b10000` (`SECTION_COLONIES` alone —
 * the only section still whole on every tick; the colony section is a fraction of a percent
 * of the bytes and is deliberately left alone). A bee's `changed` entry is no longer whole:
 * `u32 id | u8 mask | masked fields`, mask bits 0-5 naming type/position/health/food/nectar/
 * flags in that order, bits 6-7 **reserved and required to be zero** — a set reserved bit is
 * refused rather than skipped, because every field after the mask is positional against which
 * bits are set. An unmasked field carries forward untouched; a bee that changed nothing is
 * absent from `removed`/`added`/`changed` alike, which is where most of this version's saving
 * comes from at a steady-state ~60% of the population unchanged tick to tick. `bee.colony` is
 * immutable and rides the `added` entry; `bee.type_id` is not (`switch_type` mutates it) and is
 * a masked field like any other.
 *
 * **5 → 6** (`bee-state-flash`): a bee's B++ FSM **state name** reaches the client. The coarse
 * preamble gains a run-global, ordered, deduplicated **state-name table** after the flower-patch
 * table (`u16` count, then per entry a `u8` byte length and that many UTF-8 bytes), and the bee
 * entry gains a `u16 state_id` indexing it on the previously reserved field-mask **bit 6** —
 * {@link STATE_ID_NONE} (`u16::MAX`) for a bee with no B++ state at all (a bot colony's bee, every
 * queen). Bit 7 stays reserved and a set bit 7 is still refused. No migration is written: a v5
 * trace carries no table, so one would have to be invented. */
export const TRACE_FORMAT_VERSION = 6;

/** The reserved **no-B++-state** `state_id`. Pinned at `u16::MAX` rather than derived from a
 * particular run's table length: two independent decoders implement this format and their outputs
 * are asserted byte-identical across compilation targets, so a sentinel that varied with context
 * would be a constant each decoder had to re-derive identically — exactly the shape of drift the
 * cross-target parity rule exists to prevent. Mirrors `format::STATE_ID_NONE`. */
export const STATE_ID_NONE = 0xffff;

/** One index entry's fixed width: `u64 tick | u64 offset | u32 length`. Fixed width is
 * what makes locating tick *N* arithmetic rather than a scan. */
export const INDEX_ENTRY_BYTES = 20;

/** Which of the trace components **this decoder reads** a value refers to. A version-2
 * trace also declares `balance`, which the client does not decode (see the module
 * header), so it is absent from this union by design. */
export type TraceComponentName = 'coarse' | 'detail' | 'index';

/** The coarsened activity class a cluster blob and a detail bee record carry — the
 * same table the live wide tier uses (`waggle_proto::lod::ActionClass`), mirrored in
 * `format::ActivityClass` so the review screen and the Match screen classify a bee
 * identically. */
export enum ActivityClass {
  Forage = 0,
  Build = 1,
  Fight = 2,
  Idle = 3,
}

function activityFromBits(bits: number): ActivityClass {
  switch (bits) {
    case 0:
      return ActivityClass.Forage;
    case 1:
      return ActivityClass.Build;
    case 2:
      return ActivityClass.Fight;
    default:
      return ActivityClass.Idle;
  }
}

/** Raised when a manifest-declared component's bytes disagree with the manifest, or
 * cannot be decoded. Carries which component failed so the screen can name it — and,
 * crucially, so the screen never renders it as ordinary coarse-tier degradation. */
export class TraceCorruptionError extends Error {
  constructor(
    public readonly component: TraceComponentName,
    message: string,
  ) {
    super(`trace ${component} is corrupt: ${message}`);
    this.name = 'TraceCorruptionError';
  }
}

/**
 * Raised when a trace declares a format version this build does not read.
 *
 * Deliberately **not** a {@link TraceCorruptionError} and not a subclass of one: a corrupt trace is
 * *damaged*, an unsupported one is *merely old*, with nothing wrong with its bytes. Collapsing the
 * two — which this decoder did until format version 6 — means a routine version bump reports every
 * previously written trace as damage, and a caller that wants to classify a stale artifact quietly
 * cannot tell it from one worth complaining about.
 *
 * The distinction became load-bearing when traces stopped living only in a server run directory: a
 * **device-retained** practice trace is opened here, by the client, and the Rust reader never sees
 * it (`rounds-trace`, "the client's own decoder, which mirrors this format and holds its own version
 * gate").
 */
export class TraceUnsupportedVersionError extends Error {
  constructor(
    /** The version the trace declares. */
    public readonly formatVersion: number,
    /** The one version this build reads — the gate is exact equality, not a range. */
    public readonly supportedVersion: number,
  ) {
    super(
      `trace format version ${formatVersion} is not supported (this client reads ${supportedVersion})`,
    );
    this.name = 'TraceUnsupportedVersionError';
  }
}

// ---------------------------------------------------------------------------
// The manifest
// ---------------------------------------------------------------------------

/** One declared component: its relative file name, byte length, record count and
 * checksum. A trace never records an absolute path. */
export interface TraceComponentDecl {
  file: string;
  byteLen: number;
  recordCount: number;
  checksum: string;
}

/** The balance-derived denominators the detail encoding quantized against, recorded in
 * the manifest so a reader dequantizes identically. */
export interface TraceDenominators {
  fieldSize: number;
  foodMax: number;
  patchRichness: number;
  cellHp: number;
  spillMax: number;
}

/** The published trace's commit marker (`manifest.json`), camelCased. */
export interface TraceManifest {
  traceFormatVersion: number;
  /**
   * The run this trace was written by — **`null` for the tournament's shared preview**, whose
   * envelope deliberately omits it (`rounds-preview-run`: "the preview's run id MUST NOT be
   * disclosed to a member", enforced by `preview_manifest_handler` removing the field).
   *
   * Nullable rather than required, because required was wrong on the wire: the parser refused the
   * one manifest shape the preview route can ever serve, so every preview open settled `corrupt`
   * and rendered as nothing (E2E round 2, R9). Nothing in the client reads this — a caller that
   * needs a run id already holds the one it asked with, and the preview has none by design.
   */
  runId: string | null;
  engineVersion: string;
  balancePreset: string;
  balanceSchemaVersion: number;
  seed: number;
  firstTick: number;
  terminalTick: number;
  coarseCadenceTicks: number;
  /** The detail tier's keyframe cadence, in ticks — declared from format version 3 rather than
   * assumed, so a measurement can move it without a format move. Every record is a keyframe at
   * v3, so nothing here reads it yet; it exists so stage B's span replay has a cadence to anchor
   * against without another manifest field showing up mid-format. */
  keyframeCadenceTicks: number;
  denominators: TraceDenominators;
  components: Record<TraceComponentName, TraceComponentDecl>;
}

function requireNumber(source: Record<string, unknown>, key: string, where: string): number {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TraceCorruptionError('coarse', `${where}.${key} is missing or not a number`);
  }
  return value;
}

function requireString(source: Record<string, unknown>, key: string, where: string): string {
  const value = source[key];
  if (typeof value !== 'string') {
    throw new TraceCorruptionError('coarse', `${where}.${key} is missing or not a string`);
  }
  return value;
}

function requireObject(
  source: Record<string, unknown>,
  key: string,
  where: string,
): Record<string, unknown> {
  const value = source[key];
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TraceCorruptionError('coarse', `${where}.${key} is missing or not an object`);
  }
  return value as Record<string, unknown>;
}

function parseComponent(
  raw: Record<string, unknown>,
  name: TraceComponentName,
): TraceComponentDecl {
  const decl = requireObject(raw, name, 'components');
  return {
    file: requireString(decl, 'file', `components.${name}`),
    byteLen: requireNumber(decl, 'byte_len', `components.${name}`),
    recordCount: requireNumber(decl, 'record_count', `components.${name}`),
    checksum: requireString(decl, 'checksum', `components.${name}`),
  };
}

/**
 * Parses a published `manifest.json` (snake_case on disk, camelCase in the client — the
 * `waggle-proto` DTO convention). Every declared field is required: a manifest missing
 * one is not a "tolerate and carry on" case, it is a trace we refuse to open.
 */
export function parseTraceManifest(json: unknown): TraceManifest {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    throw new TraceCorruptionError('coarse', 'manifest is not a JSON object');
  }
  const raw = json as Record<string, unknown>;
  const version = requireNumber(raw, 'trace_format_version', 'manifest');
  if (version !== TRACE_FORMAT_VERSION) {
    // Its own typed condition, never the corruption error: see {@link TraceUnsupportedVersionError}.
    throw new TraceUnsupportedVersionError(version, TRACE_FORMAT_VERSION);
  }
  const denominators = requireObject(raw, 'denominators', 'manifest');
  const components = requireObject(raw, 'components', 'manifest');

  return {
    traceFormatVersion: version,
    // Absent is legitimate (the preview); present-but-not-a-string is still damage.
    runId: raw['run_id'] === undefined ? null : requireString(raw, 'run_id', 'manifest'),
    engineVersion: requireString(raw, 'engine_version', 'manifest'),
    balancePreset: requireString(raw, 'balance_preset', 'manifest'),
    balanceSchemaVersion: requireNumber(raw, 'balance_schema_version', 'manifest'),
    seed: requireNumber(raw, 'seed', 'manifest'),
    firstTick: requireNumber(raw, 'first_tick', 'manifest'),
    terminalTick: requireNumber(raw, 'terminal_tick', 'manifest'),
    coarseCadenceTicks: requireNumber(raw, 'coarse_cadence_ticks', 'manifest'),
    keyframeCadenceTicks: requireNumber(raw, 'keyframe_cadence_ticks', 'manifest'),
    denominators: {
      fieldSize: requireNumber(denominators, 'field_size', 'denominators'),
      foodMax: requireNumber(denominators, 'food_max', 'denominators'),
      patchRichness: requireNumber(denominators, 'patch_richness', 'denominators'),
      cellHp: requireNumber(denominators, 'cell_hp', 'denominators'),
      spillMax: requireNumber(denominators, 'spill_max', 'denominators'),
    },
    components: {
      coarse: parseComponent(components, 'coarse'),
      detail: parseComponent(components, 'detail'),
      index: parseComponent(components, 'index'),
    },
  };
}

// ---------------------------------------------------------------------------
// The trace-manifest response envelope (`rounds-trace-http`)
// ---------------------------------------------------------------------------
//
// `GET …/trace/manifest` no longer answers the bare `manifest.json` content: it answers
// `{ manifest, retention }` (`rounds_http::manifest_envelope`), the manifest riding
// verbatim inside it so the on-disk bytes stay A5-reproducible and are never confused
// with the response that carries them. The retention half is a **sibling in the wire
// body**, never a field inside `manifest.json` — a separate, versioned on-disk marker
// backs it server-side (`retention.json`), rendered here in this surface's own camelCase.

/** One evicted tick range, inclusive at both ends — the marker's own vocabulary, carried
 * through to the client unchanged so a future chunked detail format needs no client
 * change (`rounds-trace-retention` **H15**). */
export interface EvictedSpan {
  fromTick: number;
  toTick: number;
}

/** The detail identity's retention state, as the manifest envelope reports it. Both arms
 * carry `evictedSpans` (empty for `retained`) so a consumer never has to branch on
 * `state` before reading spans. */
export type DetailRetentionState =
  | { state: 'retained'; evictedSpans: readonly EvictedSpan[] }
  | { state: 'evicted'; evictedSpans: readonly EvictedSpan[] };

/** The trace's retention state, as the manifest envelope reports it. Only the detail
 * identity has an eviction transition in this change (`rounds-trace-retention` **H2**);
 * coarse is immortal and carries no retention state of its own. */
export interface TraceRetention {
  detail: DetailRetentionState;
}

/** The `GET …/trace/manifest` response envelope: the published manifest, verbatim, plus
 * the trace's retention state and the two figures read off its recorded balance.
 *
 * `tickRateHz` (tick-rate-single-source design **E5**) is a sibling of `manifest`, never a
 * field inside it: the manifest bytes are part of what a re-simulation must reproduce
 * byte-for-byte, so nothing is added to them. It is read server-side from the trace's own
 * **recorded balance** — the run's own tick rate, not whatever preset the serving process
 * happens to be configured with now — because a completed trace never receives a
 * subsequently published rate.
 *
 * `perCellCapacity` (WAG-100) rides beside it on both counts. A colony's stored-nectar
 * ceiling is `live cells × per-cell capacity` (SIM-RULES §6): the cells are in every detail
 * record already, and this is the multiplier the client would otherwise have to invent. */
export interface TraceManifestResponse {
  manifest: TraceManifest;
  retention: TraceRetention;
  tickRateHz: number;
  perCellCapacity: number;
}

function parseEvictedSpan(raw: unknown, where: string): EvictedSpan {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TraceCorruptionError('coarse', `${where} is missing or not an object`);
  }
  const row = raw as Record<string, unknown>;
  return {
    fromTick: requireNumber(row, 'fromTick', where),
    toTick: requireNumber(row, 'toTick', where),
  };
}

function parseEvictedSpans(raw: unknown, where: string): EvictedSpan[] {
  if (!Array.isArray(raw)) {
    throw new TraceCorruptionError('coarse', `${where} is missing or not an array`);
  }
  return raw.map((entry, i) => parseEvictedSpan(entry, `${where}[${i}]`));
}

function parseDetailRetention(raw: unknown): DetailRetentionState {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TraceCorruptionError('coarse', 'retention.detail is missing or not an object');
  }
  const row = raw as Record<string, unknown>;
  const evictedSpans = parseEvictedSpans(row['evictedSpans'], 'retention.detail.evictedSpans');
  const state = row['state'];
  if (state === 'retained') {
    return { state: 'retained', evictedSpans };
  }
  if (state === 'evicted') {
    return { state: 'evicted', evictedSpans };
  }
  // The trap `parseTraceManifest`'s sibling-field precedent would fall into: a perfectly
  // well-formed object whose *value* is outside the vocabulary this build knows. Reading
  // by key and silently defaulting to "retained" here would be exactly the parsed-but-
  // not-carried defect architecture-review **F3** found — so an unknown state is refused,
  // never guessed.
  throw new TraceCorruptionError(
    'coarse',
    `retention.detail.state ${JSON.stringify(state)} is not one this client knows`,
  );
}

function parseTraceRetention(raw: unknown): TraceRetention {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TraceCorruptionError('coarse', 'retention is missing or not an object');
  }
  const row = raw as Record<string, unknown>;
  return { detail: parseDetailRetention(row['detail']) };
}

/**
 * Parses the `GET …/trace/manifest` response **envelope** — `{ manifest, retention,
 * tickRateHz }` — into the immutable manifest (parsed exactly as {@link parseTraceManifest}
 * always has) plus the trace's retention state and its recorded tick rate alongside it.
 *
 * A response whose retention state cannot be fully parsed is refused **exactly as** a
 * manifest missing a declared field is: `TraceCorruptionError` propagates rather than the
 * caller falling back to treating it as "retained". A signal that is parsed but not
 * carried — the sibling field alone, silently dropped by a read-by-key parser — is
 * architecture-review **F3**'s defect; this function is the fix, so no caller may bypass
 * it and read `.manifest` off a raw response body directly.
 *
 * `tickRateHz` is **required** (tick-rate-single-source task 4.3, design **E5**): a
 * response missing it is a parse refusal, never a silently substituted 15 — the exact
 * silent-wrong-answer class the 15→5 Hz experiment on this branch produced (a 30-minute
 * trace rendering as `10:00`). `requireNumber` is the same refusal path the manifest's own
 * fields already use, and `perCellCapacity` (WAG-100) is required on the same terms: a
 * substituted 30 would print a hive ceiling this build believes in rather than the one the
 * run was played under.
 */
export function parseTraceManifestResponse(json: unknown): TraceManifestResponse {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    throw new TraceCorruptionError('coarse', 'manifest response is not a JSON object');
  }
  const envelope = json as Record<string, unknown>;
  const manifest = parseTraceManifest(envelope['manifest']);
  const retention = parseTraceRetention(envelope['retention']);
  const tickRateHz = requireNumber(envelope, 'tickRateHz', 'manifest response');
  const perCellCapacity = requireNumber(envelope, 'perCellCapacity', 'manifest response');
  return { manifest, retention, tickRateHz, perCellCapacity };
}

// ---------------------------------------------------------------------------
// Quantization (the inverses of `format::quantize_*`)
// ---------------------------------------------------------------------------

/** Inverse of `format::quantize_position`: a `u16` grid value back to a world
 * coordinate over `[-field/2, +field/2]`. */
export function dequantizePosition(q: number, fieldSize: number): number {
  return (q / 65_535) * fieldSize - fieldSize / 2;
}

/** Inverse of `format::quantize_fraction`: a `u8` fraction back onto its denominator. */
export function dequantizeFraction(q: number, max: number): number {
  return (q / 255) * max;
}

// ---------------------------------------------------------------------------
// Length framing + a bounds-checked cursor
// ---------------------------------------------------------------------------

/** A framed record: its payload and the total framed byte width (`4 + payload`). */
export interface Framed {
  payload: Uint8Array;
  framedLength: number;
}

/**
 * Strips one length frame (`u32` byte length + payload) from the front of `bytes`.
 * Returns `null` when the header is short or claims more bytes than are present — the
 * caller decides whether that is corruption or simply the end of a component.
 */
export function unframe(bytes: Uint8Array): Framed | null {
  if (bytes.byteLength < 4) {
    return null;
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const length = view.getUint32(0, true);
  const end = 4 + length;
  if (end > bytes.byteLength) {
    return null;
  }
  return { payload: bytes.subarray(4, end), framedLength: end };
}

/** A bounds-checked little-endian read cursor: every read past the end throws, so a
 * truncated record is a typed corruption error rather than a `NaN`. */
class Cursor {
  private pos = 0;
  private readonly view: DataView;

  constructor(
    private readonly bytes: Uint8Array,
    private readonly component: TraceComponentName,
  ) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  private need(n: number): number {
    const at = this.pos;
    if (at + n > this.bytes.byteLength) {
      throw new TraceCorruptionError(
        this.component,
        `record truncated: wanted ${n} byte(s) at offset ${at} of ${this.bytes.byteLength}`,
      );
    }
    this.pos = at + n;
    return at;
  }

  u8(): number {
    return this.view.getUint8(this.need(1));
  }
  u16(): number {
    return this.view.getUint16(this.need(2), true);
  }
  u32(): number {
    return this.view.getUint32(this.need(4), true);
  }
  u64(): number {
    // Ticks and offsets are well inside `Number.MAX_SAFE_INTEGER` for any real trace;
    // a value beyond it is corruption, not a bigint the screen should carry around.
    const value = this.view.getBigUint64(this.need(8), true);
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new TraceCorruptionError(this.component, `u64 value ${value} exceeds safe integer range`);
    }
    return Number(value);
  }
  f32(): number {
    return this.view.getFloat32(this.need(4), true);
  }
  bool(): boolean {
    return this.u8() !== 0;
  }
  /** `n` raw bytes, bounds-checked like every other read. Mirrors `format::Cursor::take`. */
  take(n: number): Uint8Array {
    const at = this.need(n);
    return this.bytes.subarray(at, at + n);
  }
  atEnd(): boolean {
    return this.pos >= this.bytes.byteLength;
  }
  /** Bytes left unread. Used where a caller needs to distinguish *which* field ran out —
   * the patch section's length check names how many patches it actually found, which a bare
   * `need()` throw cannot say because it does not know it is reading a patch. */
  remaining(): number {
    return this.bytes.byteLength - this.pos;
  }
  requireAtEnd(what: string): void {
    if (!this.atEnd()) {
      throw new TraceCorruptionError(
        this.component,
        `${what} has ${this.bytes.byteLength - this.pos} trailing byte(s)`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// The index
// ---------------------------------------------------------------------------

/** One index entry: the tick, its record's byte offset into `detail.bin`, and that
 * record's **framed** byte length, so `offset..offset+length` is self-describing. */
export interface IndexEntry {
  tick: number;
  offset: number;
  length: number;
}

/** Decodes the whole fixed-width index. A trailing partial entry is corruption. */
export function decodeIndex(bytes: Uint8Array): IndexEntry[] {
  if (bytes.byteLength % INDEX_ENTRY_BYTES !== 0) {
    throw new TraceCorruptionError(
      'index',
      `${bytes.byteLength} bytes is not a whole number of ${INDEX_ENTRY_BYTES}-byte entries`,
    );
  }
  const cursor = new Cursor(bytes, 'index');
  const entries: IndexEntry[] = [];
  while (!cursor.atEnd()) {
    entries.push({ tick: cursor.u64(), offset: cursor.u64(), length: cursor.u32() });
  }
  return entries;
}

// ---------------------------------------------------------------------------
// The coarse tier
// ---------------------------------------------------------------------------

/** One colony's sampled statistics in a coarse sample. */
export interface ColonyStat {
  colony: number;
  alive: boolean;
  population: number;
  pool: number;
}

/** One cluster blob: which colony/supercell, the members' centroid, the member count
 * and the dominant activity class. */
export interface ClusterBlob {
  colony: number;
  cell: [number, number];
  members: number;
  centroid: [number, number];
  activity: ActivityClass;
}

/** One sampled tick of the coarse tier. */
export interface CoarseSample {
  tick: number;
  colonies: ColonyStat[];
  clusters: ClusterBlob[];
}

/** The coarse tier's record-kind byte: `0` = the match-static preamble, `1` = a sampled
 * tick. Read first, so a reader that expects only samples cannot decode the preamble as
 * one. */
const COARSE_KIND_PREAMBLE = 0;
const COARSE_KIND_SAMPLE = 1;

/** Strict UTF-8, for the preamble's state-name table. `fatal` so malformed bytes throw rather than
 * becoming replacement characters — see {@link decodeCoarsePreamble}. */
const UTF8 = new TextDecoder('utf-8', { fatal: true });

/** One flower patch's **match-static** identity — what never changes about it for the
 * whole run. Carried once in the coarse tier's preamble instead of on every tick's patch
 * entry. */
export interface PreamblePatch {
  id: number;
  /** `u16`-quantized against the manifest's `field_size`, exactly as every other position. */
  position: [number, number];
}

/** The coarse tier's leading preamble: the flower-patch table, in ascending `LogicalId`
 * order — the same order the per-tick patch section is written in, and indexed against
 * by position rather than by id — and, from format version 6, the state-name table. */
export interface CoarsePreamble {
  patches: readonly PreamblePatch[];
  /**
   * The run-global **state-name table** (format version 6+), which the detail tier's per-bee
   * `state_id` indexes positionally. Derived inside `waggle-round` at roster assembly in
   * `ColonyId` → type → state-index order, deduplicated on first occurrence, so two colonies
   * declaring the same state name share one id.
   *
   * Empty for a run whose colonies are all bot fixtures: they export no state names, so there is
   * nothing to intern and every bee carries {@link STATE_ID_NONE}.
   */
  stateNames: readonly string[];
}

/** A decoded coarse record, kind included. The tier holds exactly one preamble, at the
 * head, followed by samples — mirrors `format::CoarseRecord`. */
export type CoarseRecord =
  | { kind: 'preamble'; preamble: CoarsePreamble }
  | { kind: 'sample'; sample: CoarseSample };

/** The whole coarse component, decoded: the leading preamble plus every sample after it. */
export interface DecodedCoarse {
  preamble: CoarsePreamble;
  samples: CoarseSample[];
}

function decodeCoarsePreamble(payload: Uint8Array): CoarsePreamble {
  const c = new Cursor(payload, 'coarse');
  const kind = c.u8();
  if (kind !== COARSE_KIND_PREAMBLE) {
    throw new TraceCorruptionError(
      'coarse',
      `expected the leading preamble record (kind ${COARSE_KIND_PREAMBLE}), got kind ${kind}`,
    );
  }
  const patchCount = c.u32();
  const patches: PreamblePatch[] = [];
  for (let i = 0; i < patchCount; i++) {
    patches.push({ id: c.u32(), position: [c.u16(), c.u16()] });
  }
  // The state-name table (format version 6+): `u16 count | { u8 len, len × UTF-8 } × count`. The
  // count's width needs no bound of its own — it *is* the bound, and it matches the `state_id`
  // domain the table is indexed by.
  const nameCount = c.u16();
  const stateNames: string[] = [];
  for (let i = 0; i < nameCount; i++) {
    const raw = c.take(c.u8());
    try {
      // `fatal`, not lossy: the table is what every label in the run resolves through, so a name
      // that is not what was written is not a name — the same refusal the Rust decoder makes.
      stateNames.push(UTF8.decode(raw));
    } catch {
      throw new TraceCorruptionError('coarse', `state name ${i} is not valid UTF-8`);
    }
  }
  c.requireAtEnd('coarse preamble');
  return { patches, stateNames };
}

function decodeCoarseSample(payload: Uint8Array): CoarseSample {
  const c = new Cursor(payload, 'coarse');
  const kind = c.u8();
  if (kind !== COARSE_KIND_SAMPLE) {
    throw new TraceCorruptionError(
      'coarse',
      `expected a sample record (kind ${COARSE_KIND_SAMPLE}), got kind ${kind}`,
    );
  }
  const tick = c.u64();
  const colonyCount = c.u16();
  const colonies: ColonyStat[] = [];
  for (let i = 0; i < colonyCount; i++) {
    colonies.push({ colony: c.u16(), alive: c.bool(), population: c.u32(), pool: c.f32() });
  }
  const clusterCount = c.u32();
  const clusters: ClusterBlob[] = [];
  for (let i = 0; i < clusterCount; i++) {
    clusters.push({
      colony: c.u16(),
      cell: [c.u16(), c.u16()],
      members: c.u32(),
      centroid: [c.f32(), c.f32()],
      activity: activityFromBits(c.u8()),
    });
  }
  c.requireAtEnd('coarse sample');
  return { tick, colonies, clusters };
}

/** Decodes one coarse record, **the kind byte first**, so a preamble is never mistaken for
 * a sample or the reverse. Throws on a kind this client does not know, rather than
 * guessing at a forward-dated record. */
export function decodeCoarseRecord(payload: Uint8Array): CoarseRecord {
  if (payload.byteLength < 1) {
    throw new TraceCorruptionError('coarse', 'a coarse record is empty; the leading kind byte is missing');
  }
  const kind = payload[0]!;
  if (kind === COARSE_KIND_PREAMBLE) {
    return { kind: 'preamble', preamble: decodeCoarsePreamble(payload) };
  }
  if (kind === COARSE_KIND_SAMPLE) {
    return { kind: 'sample', sample: decodeCoarseSample(payload) };
  }
  throw new TraceCorruptionError('coarse', `record kind ${kind} is not one this client knows`);
}

/**
 * Decodes the whole coarse component — it is downloaded whole by design.
 *
 * The **first** record must be the preamble: it is the table every per-tick patch section
 * indexes into, so a tier that opened with a sample, or that carried a second preamble
 * anywhere, could not be replayed consistently and is refused rather than tolerated.
 */
export function decodeCoarse(bytes: Uint8Array): DecodedCoarse {
  let preamble: CoarsePreamble | null = null;
  const samples: CoarseSample[] = [];
  let rest = bytes;
  while (rest.byteLength > 0) {
    const framed = unframe(rest);
    if (framed === null) {
      throw new TraceCorruptionError(
        'coarse',
        `truncated length frame ${rest.byteLength} byte(s) from the end`,
      );
    }
    const record = decodeCoarseRecord(framed.payload);
    if (record.kind === 'preamble') {
      if (preamble !== null) {
        throw new TraceCorruptionError(
          'coarse',
          'the coarse tier carries more than one preamble record',
        );
      }
      preamble = record.preamble;
    } else {
      if (preamble === null) {
        throw new TraceCorruptionError(
          'coarse',
          'the coarse tier opens with a sample rather than the leading patch-table preamble',
        );
      }
      samples.push(record.sample);
    }
    rest = rest.subarray(framed.framedLength);
  }
  if (preamble === null) {
    throw new TraceCorruptionError(
      'coarse',
      'the coarse tier is empty; it must open with the patch-table preamble',
    );
  }
  return { preamble, samples };
}

// ---------------------------------------------------------------------------
// The detail tier
// ---------------------------------------------------------------------------

/** One bee's close-zoom state. Positions are `u16`-quantized to the field grid; the
 * three bars are `u8` fractions of their own per-bee denominators. */
export interface DetailBee {
  id: number;
  colony: number;
  /** The `ColonyTypeId`, or `null` for the typeless queen (`0xFF` on the wire). */
  typeId: number | null;
  position: [number, number];
  health: number;
  food: number;
  nectar: number;
  gathering: boolean;
  activity: ActivityClass;
  /**
   * The bee's B++ FSM state, interned against the coarse preamble's state-name table (format
   * version 6+), or {@link STATE_ID_NONE} when it has no B++ state at all — every queen, and every
   * bee of a bot colony running a Rust fixture brain.
   *
   * Guaranteed by the decoder to be either the sentinel or a valid index into
   * {@link CoarsePreamble.stateNames}: an id that is neither is a corrupt trace, refused at decode
   * rather than resolved to a plausible value here.
   */
  stateId: number;
}

/** One flower patch's close-zoom state. */
export interface DetailPatch {
  id: number;
  position: [number, number];
  pool: number;
  dead: boolean;
}

/** One ground spill's close-zoom state. */
export interface DetailSpill {
  id: number;
  position: [number, number];
  nectar: number;
}

/** One hive cell's close-zoom state. */
export interface DetailCell {
  id: number;
  colony: number;
  position: [number, number];
  hp: number;
  occupied: boolean;
}

/** One colony's per-tick economy state — the full-precision pool the coarse tier only
 * samples. */
export interface DetailColony {
  colony: number;
  alive: boolean;
  pool: number;
}

/** One tick's detail record. */
export interface DetailRecord {
  tick: number;
  bees: DetailBee[];
  patches: DetailPatch[];
  spills: DetailSpill[];
  cells: DetailCell[];
  colonies: DetailColony[];
}

/** One bit per detail section, in the order the sections are encoded. Mirrors
 * `format::SECTION_*`: a record's mask sets the bit of every section it carries in
 * **keyframe** (full) form; a clear bit means that section is delta-encoded against the
 * record's `base_tick`. Only `SECTION_COLONIES` is still referenced by name on this side —
 * the only section a delta record ever keyframes from format version 5 on — the rest are
 * folded straight into the two literal mask values below. */
const SECTION_COLONIES = 1 << 4;

/** Every section keyframed — what a full record carries, and the only shape format version
 * 3 ever wrote. Mirrors `format::ALL_SECTIONS_KEYFRAMED`. */
const ALL_SECTIONS_KEYFRAMED = 0b11111;

/** The mask a **delta** record carries from format version 5: only the per-colony economy
 * section stays whole every tick — it is a fraction of a percent of the bytes, and
 * delta-encoding it would cost more in complexity than it could ever return. Everything else —
 * bees (from this version on), patches, spills, cells — is delta-encoded against the record's
 * `base_tick`. Mirrors `format::DELTA_SECTION_MASK`, which dropped `SECTION_BEES` at the same
 * version this bit did. */
const DELTA_SECTION_MASK = SECTION_COLONIES;

/** The bee `changed` entry's field-mask bits (design **E3**). Bit 7 carries no field and **must be
 * zero**; a set reserved bit is a record from a format this build does not read, and is refused
 * rather than skipped — see {@link applyBeeChange}. Mirrors `format::BEE_FIELD_*`.
 *
 * Bit 6 was reserved until format version 6 and now names the interned `state_id`. That is the
 * lifecycle the reserved-bit rule exists to serve, and it composes: bit 7's intended future is "an
 * extension mask byte follows", which a v6 reader still correctly refuses because it genuinely
 * cannot read such a record. */
const BEE_FIELD_TYPE = 1 << 0;
const BEE_FIELD_POSITION = 1 << 1;
const BEE_FIELD_HEALTH = 1 << 2;
const BEE_FIELD_FOOD = 1 << 3;
const BEE_FIELD_NECTAR = 1 << 4;
const BEE_FIELD_FLAGS = 1 << 5;
/** The interned `state_id`, from format version 6. */
const BEE_FIELD_STATE = 1 << 6;
/** Every assigned bee field-mask bit; the complement is reserved and must be zero. Mirrors
 * `format::BEE_FIELD_MASK_ASSIGNED`. */
const BEE_FIELD_MASK_ASSIGNED =
  BEE_FIELD_TYPE |
  BEE_FIELD_POSITION |
  BEE_FIELD_HEALTH |
  BEE_FIELD_FOOD |
  BEE_FIELD_NECTAR |
  BEE_FIELD_FLAGS |
  BEE_FIELD_STATE;

/**
 * One `state_id` read, checked against the table it indexes.
 *
 * An in-range id that is neither {@link STATE_ID_NONE} nor a valid index means the trace disagrees
 * with itself, and is a **corrupt trace** on both readers — never no-label, an empty string or
 * `undefined`. Resolving it to "no state" would render corruption as a gameplay fact, and a bee
 * reported stateless is indistinguishable from a bot's.
 */
function readStateId(c: Cursor, preamble: CoarsePreamble): number {
  const stateId = c.u16();
  if (stateId !== STATE_ID_NONE && stateId >= preamble.stateNames.length) {
    throw new TraceCorruptionError(
      'detail',
      `a bee's state_id ${stateId} is neither the no-state sentinel nor an index into the ` +
        `preamble's ${preamble.stateNames.length} state name(s)`,
    );
  }
  return stateId;
}

/** Unpacks a bee's `flags` byte: `gathering` in bit 0, the [`ActivityClass`] in bits 1-2.
 * The inverse of `format::pack_bee_flags`. */
function unpackBeeFlags(flags: number): { gathering: boolean; activity: ActivityClass } {
  return { gathering: (flags & 1) !== 0, activity: activityFromBits((flags >> 1) & 0b11) };
}

/** The bee entry's full 17 B keyframe form — used both for a keyframe record's whole bee
 * array and for a delta's `added` entries, exactly as `format::decode_bee` is. */
function decodeBeeFull(c: Cursor, preamble: CoarsePreamble): DetailBee {
  const id = c.u32();
  const colony = c.u16();
  const rawType = c.u8();
  const position: [number, number] = [c.u16(), c.u16()];
  const health = c.u8();
  const food = c.u8();
  const nectar = c.u8();
  const { gathering, activity } = unpackBeeFlags(c.u8());
  const stateId = readStateId(c, preamble);
  return {
    id,
    colony,
    typeId: rawType === 0xff ? null : rawType,
    position,
    health,
    food,
    nectar,
    gathering,
    activity,
    stateId,
  };
}

/**
 * Applies one `changed` bee entry — `u8 mask | masked fields, in mask-bit order` — to the bee
 * carried forward from the base record. `colony` is never touched: it is immutable and rides
 * the `added` entry that introduced the bee, never a `changed` one. `typeId` **is** a masked
 * field like any other — `switch_type` mutates it, so it cannot be hoisted out of the per-tick
 * record the way `colony` was. An unmasked field is carried forward untouched, never zeroed.
 *
 * @throws {TraceCorruptionError} named `'detail'` when reserved bit 7 is set, or when a masked
 *   `state_id` indexes past the preamble's table. The reserved bit is refused rather than skipped:
 *   every field after the mask is positional against which bits are set, so ignoring an unknown
 *   selector would read the *next* field from the wrong offset and hand back a plausible bee that
 *   never existed.
 */
function applyBeeChange(c: Cursor, bee: DetailBee, preamble: CoarsePreamble): void {
  const mask = c.u8();
  if ((mask & ~BEE_FIELD_MASK_ASSIGNED) !== 0) {
    throw new TraceCorruptionError(
      'detail',
      `a changed bee entry sets reserved field-mask bit(s) in 0b${mask.toString(2).padStart(8, '0')}`,
    );
  }
  if (mask & BEE_FIELD_TYPE) {
    const raw = c.u8();
    bee.typeId = raw === 0xff ? null : raw;
  }
  if (mask & BEE_FIELD_POSITION) {
    bee.position = [c.u16(), c.u16()];
  }
  if (mask & BEE_FIELD_HEALTH) {
    bee.health = c.u8();
  }
  if (mask & BEE_FIELD_FOOD) {
    bee.food = c.u8();
  }
  if (mask & BEE_FIELD_NECTAR) {
    bee.nectar = c.u8();
  }
  if (mask & BEE_FIELD_FLAGS) {
    const { gathering, activity } = unpackBeeFlags(c.u8());
    bee.gathering = gathering;
    bee.activity = activity;
  }
  // Bit 6 clear means the bee's state did not move, so the entry carries no `state_id` bytes and
  // the previous value is carried forward — which is where this field's byte saving comes from,
  // and emphatically **not** a change signal: every keyframe restates all sections whole, so a
  // consumer detecting state changes compares decoded *values* (design **E10**).
  if (mask & BEE_FIELD_STATE) {
    bee.stateId = readStateId(c, preamble);
  }
}

function decodeSpillFull(c: Cursor): DetailSpill {
  return { id: c.u32(), position: [c.u16(), c.u16()], nectar: c.u8() };
}

function decodeCellFull(c: Cursor): DetailCell {
  return {
    id: c.u32(),
    colony: c.u16(),
    position: [c.u16(), c.u16()],
    hp: c.u8(),
    occupied: c.bool(),
  };
}

/** The **keyframe** form of the patch section: no count, no ids, no positions — a bare
 * fixed-order `(pool, dead)` array read against the preamble's own patch table, in the same
 * ascending-`LogicalId` order the preamble was written in.
 *
 * @throws {TraceCorruptionError} named `'detail'` when the section runs short of the
 *   preamble's patch count — the record and the preamble describe different worlds, which
 *   is worth saying specifically rather than folding into a generic truncation message. */
function decodeKeyframePatches(c: Cursor, preamble: CoarsePreamble): DetailPatch[] {
  const patches: DetailPatch[] = [];
  for (const entry of preamble.patches) {
    if (c.remaining() < 2) {
      throw new TraceCorruptionError(
        'detail',
        `the patch section names ${preamble.patches.length} patch(es) from the preamble but ` +
          `only supplies ${patches.length}: the record and the preamble describe different worlds`,
      );
    }
    patches.push({ id: entry.id, position: entry.position, pool: c.u8(), dead: c.bool() });
  }
  return patches;
}

/** The **delta** form of the patch section: `u32 n, { u16 index, u8 pool, u8 dead } × n`.
 * Patches have no `removed`/`added` at all — the set is fixed for the run, so the only thing
 * that can move is a value, named by its position in the preamble's table. */
function applyPatchDelta(c: Cursor, basePatches: readonly DetailPatch[]): DetailPatch[] {
  const patches = basePatches.map((patch) => ({ ...patch }));
  const changedCount = c.u32();
  for (let i = 0; i < changedCount; i++) {
    const index = c.u16();
    const pool = c.u8();
    const dead = c.bool();
    const patch = patches[index];
    if (patch === undefined) {
      throw new TraceCorruptionError(
        'detail',
        `a delta names patch index ${index}, outside the preamble's ${patches.length} patch(es)`,
      );
    }
    patch.pool = pool;
    patch.dead = dead;
  }
  return patches;
}

/**
 * Applies one delta-encoded section's three lists — `removed`, `added`, `changed`, in that
 * order — to `base`, returning the section ascending by `id`. Mirrors `format::apply_delta`.
 *
 * **A removal is named, never inferred.** An entity absent from `changed` is carried forward
 * unchanged from `base`; only an id in `removed` makes it gone — which is what stops a
 * dropped or truncated record from reading as a silent despawn.
 *
 * @throws {TraceCorruptionError} named `'detail'` when a `changed` entry names an id the span
 *   does not hold: the encoder only ever names one it carried forward or added, so such a
 *   record describes a world this reader is not in.
 */
function applyEntityDelta<T extends { id: number }>(
  c: Cursor,
  base: readonly T[],
  decodeFull: (c: Cursor) => T,
  applyChanged: (c: Cursor, entity: T) => void,
): T[] {
  const live = new Map<number, T>();
  for (const entity of base) {
    live.set(entity.id, { ...entity });
  }
  const removedCount = c.u32();
  for (let i = 0; i < removedCount; i++) {
    live.delete(c.u32());
  }
  const addedCount = c.u32();
  for (let i = 0; i < addedCount; i++) {
    const entity = decodeFull(c);
    live.set(entity.id, entity);
  }
  const changedCount = c.u32();
  for (let i = 0; i < changedCount; i++) {
    const id = c.u32();
    const entity = live.get(id);
    if (entity === undefined) {
      throw new TraceCorruptionError(
        'detail',
        `a changed entry names id ${id}, which this span does not hold`,
      );
    }
    applyChanged(c, entity);
  }
  // `Map` preserves insertion order, not numeric order — the ascending-`id` order every other
  // section carries has to be imposed explicitly here, exactly as the encoder's own
  // `BTreeMap` imposes it on the write side.
  return [...live.values()].sort((a, b) => a.id - b.id);
}

/**
 * Decodes one detail payload against `preamble` and, for a delta record, `base` — the
 * previously replayed record in its span. `base === null` means "no predecessor available",
 * which is correct for the first record fed to a {@link SpanReplayer} and for
 * {@link decodeDetailRecord}'s single-record use.
 *
 * Mirrors `format::decode_one`: the mask is read first and must be one of the two values this
 * client knows; a keyframe ignores `base` entirely (it is self-contained by construction), a
 * delta requires one and refuses when it disagrees with the delta's own `base_tick`.
 *
 * @throws {TraceCorruptionError} named `'detail'` — an unknown section mask, a delta fed with
 *   no predecessor ("not anchored"), a delta whose `base_tick` is not `base`'s own tick, a
 *   patch-section length disagreement, a `changed` entry naming an unheld entity, or a
 *   changed bee entry with a reserved field-mask bit set.
 */
function decodeOne(
  payload: Uint8Array,
  preamble: CoarsePreamble,
  base: DetailRecord | null,
): DetailRecord {
  const c = new Cursor(payload, 'detail');
  const mask = c.u8();
  if (mask !== ALL_SECTIONS_KEYFRAMED && mask !== DELTA_SECTION_MASK) {
    // A mask this version does not write is a record from a format this build does not read.
    // Refused whole rather than partially decoded: a forward-dated record whose unknown
    // sections were skipped would be exactly the plausible-but-false replay the format forbids.
    throw new TraceCorruptionError(
      'detail',
      `section keyframe mask ${mask} is not one this client knows (expected ` +
        `${ALL_SECTIONS_KEYFRAMED} or ${DELTA_SECTION_MASK})`,
    );
  }
  const tick = c.u64();

  let effectiveBase: DetailRecord | null = null;
  if (mask !== ALL_SECTIONS_KEYFRAMED) {
    const baseTick = c.u64();
    // A delta record at the head of a span has nothing to apply to — the span was located
    // wrongly (the computed anchor did not land on a keyframe), not merely torn.
    if (base === null) {
      throw new TraceCorruptionError(
        'detail',
        `a span's first record carries section mask ${mask} rather than a full keyframe`,
      );
    }
    if (base.tick !== baseTick) {
      throw new TraceCorruptionError(
        'detail',
        `a delta record names base tick ${baseTick} where its predecessor is ${base.tick}`,
      );
    }
    effectiveBase = base;
  }

  // From format version 5, bees join the three-list scheme too — the same shape as spills and
  // cells, and in the same position (first) the encoder writes them in.
  let bees: DetailBee[];
  let patches: DetailPatch[];
  let spills: DetailSpill[];
  let cells: DetailCell[];
  if (effectiveBase === null) {
    const beeCount = c.u32();
    bees = [];
    for (let i = 0; i < beeCount; i++) {
      bees.push(decodeBeeFull(c, preamble));
    }
    patches = decodeKeyframePatches(c, preamble);
    const spillCount = c.u32();
    spills = [];
    for (let i = 0; i < spillCount; i++) {
      spills.push(decodeSpillFull(c));
    }
    const cellCount = c.u32();
    cells = [];
    for (let i = 0; i < cellCount; i++) {
      cells.push(decodeCellFull(c));
    }
  } else {
    bees = applyEntityDelta(
      c,
      effectiveBase.bees,
      (cur) => decodeBeeFull(cur, preamble),
      (cur, bee) => applyBeeChange(cur, bee, preamble),
    );
    patches = applyPatchDelta(c, effectiveBase.patches);
    spills = applyEntityDelta(c, effectiveBase.spills, decodeSpillFull, (cur, spill) => {
      spill.nectar = cur.u8();
    });
    cells = applyEntityDelta(c, effectiveBase.cells, decodeCellFull, (cur, cell) => {
      cell.hp = cur.u8();
      cell.occupied = cur.bool();
    });
  }

  // Colonies: whole, every tick, in both the keyframe and the delta shape — the same position
  // in the byte stream either way.
  const colonyCount = c.u16();
  const colonies: DetailColony[] = [];
  for (let i = 0; i < colonyCount; i++) {
    colonies.push({ colony: c.u16(), alive: c.bool(), pool: c.f32() });
  }

  c.requireAtEnd('detail record');
  return { tick, bees, patches, spills, cells, colonies };
}

/**
 * Decodes one **self-contained keyframe** detail payload against `preamble`. Refuses a delta
 * record (there is no predecessor to apply it to) exactly as `format::decode_detail_with`
 * does — a delta needs a {@link SpanReplayer}, which is what every real reader of the detail
 * tier uses.
 *
 * @throws {TraceCorruptionError} — see {@link decodeOne}.
 */
export function decodeDetailRecord(payload: Uint8Array, preamble: CoarsePreamble): DetailRecord {
  return decodeOne(payload, preamble, null);
}

/**
 * Replays a **keyframe-anchored span**: the decoder's counterpart to `format::DetailEncoder`
 * and a mirror of `format::SpanReplayer`.
 *
 * A span is a keyframe record followed by the delta records that depend on it. Records are fed
 * in tick order, and each one yields the whole logical {@link DetailRecord} for its tick — so a
 * caller answering a range replays from the anchor and keeps only the ticks it was asked for.
 *
 * The replayer **verifies** rather than assumes: the first record fed must be a keyframe, and
 * every delta's `base_tick` must be its immediate predecessor. Both are refused typed, because
 * the alternative is serving a world that never existed.
 */
export class SpanReplayer {
  private currentRecord: DetailRecord | null = null;

  /** A replayer over `preamble`'s patch table, positioned before a span's anchor. */
  constructor(private readonly preamble: CoarsePreamble) {}

  /**
   * Feeds the next record's payload (the bytes **inside** its length frame) and returns the
   * whole logical record for that tick.
   *
   * @throws {TraceCorruptionError} — see {@link decodeOne}.
   */
  feed(payload: Uint8Array): DetailRecord {
    const record = decodeOne(payload, this.preamble, this.currentRecord);
    this.currentRecord = record;
    return record;
  }

  /** The most recently replayed record, if any. */
  current(): DetailRecord | null {
    return this.currentRecord;
  }
}

/**
 * The tick of the keyframe governing `tick`: `firstTick + n·keyframeCadenceTicks`,
 * **first-tick anchored** — mirrors `TraceReader::span_anchor`. Arithmetic, so locating a
 * span stays O(1); the record actually found there is then *verified* to be a keyframe by
 * {@link SpanReplayer} rather than trusted, because an anchor computed from a manifest and a
 * record written by a writer are two different claims about the same tick.
 */
export function spanAnchor(tick: number, firstTick: number, keyframeCadenceTicks: number): number {
  const cadence = Math.max(1, keyframeCadenceTicks);
  if (tick < firstTick) {
    return firstTick;
  }
  return firstTick + Math.floor((tick - firstTick) / cadence) * cadence;
}

/**
 * Replays the keyframe-anchored span covering the inclusive range `[fromTick, toTick]` and
 * returns **only** the requested ticks — a mirror of `DetailAccess::replay_span`.
 *
 * `index` is the whole detail index, ascending; `readBytes` returns the bytes at a byte
 * offset/length **into the same address space `index`'s offsets are in** (the whole resident
 * `detail.bin` for {@link openTrace}, a byte range already fetched for `rounds-http`'s window).
 * A request wholly outside the index's own tick range answers with no records — **absent**, a
 * normal result — rather than an error.
 *
 * @throws {TraceCorruptionError} — a torn frame, an index entry naming a tick other than the
 *   record it points at, or anything {@link SpanReplayer.feed} refuses.
 */
export function replayDetailSpan(
  index: readonly IndexEntry[],
  readBytes: (offset: number, length: number) => Uint8Array,
  preamble: CoarsePreamble,
  firstTick: number,
  keyframeCadenceTicks: number,
  fromTick: number,
  toTick: number,
): DetailRecord[] {
  if (index.length === 0) {
    return [];
  }
  const firstPresent = index[0]!.tick;
  const lastPresent = index[index.length - 1]!.tick;
  if (toTick < firstPresent || fromTick > lastPresent) {
    return [];
  }
  const clampedFrom = Math.max(fromTick, firstPresent);
  const clampedTo = Math.min(toTick, lastPresent);
  const anchor = Math.max(spanAnchor(clampedFrom, firstTick, keyframeCadenceTicks), firstPresent);

  // The index is validated strictly ascending at every caller of this function, so the ticks
  // in `[anchor, clampedTo]` are exactly the entries between these two partition points.
  let start = 0;
  while (start < index.length && index[start]!.tick < anchor) {
    start += 1;
  }
  let end = start;
  while (end < index.length && index[end]!.tick <= clampedTo) {
    end += 1;
  }
  if (start >= end) {
    return [];
  }

  const firstEntry = index[start]!;
  const lastEntry = index[end - 1]!;
  const bytes = readBytes(firstEntry.offset, lastEntry.offset + lastEntry.length - firstEntry.offset);

  const replayer = new SpanReplayer(preamble);
  const records: DetailRecord[] = [];
  let rest = bytes;
  for (let i = start; i < end; i++) {
    const entry = index[i]!;
    const framed = unframe(rest);
    if (framed === null) {
      throw new TraceCorruptionError('detail', `truncated length frame at tick ${entry.tick}`);
    }
    const record = replayer.feed(framed.payload);
    if (record.tick !== entry.tick) {
      throw new TraceCorruptionError(
        'detail',
        `index names tick ${entry.tick} but the record carries tick ${record.tick}`,
      );
    }
    if (record.tick >= clampedFrom) {
      records.push(record);
    }
    rest = rest.subarray(framed.framedLength);
  }
  return records;
}

// ---------------------------------------------------------------------------
// Opening a whole trace
// ---------------------------------------------------------------------------

/** A published trace's manifest plus its three component blobs. */
export interface TraceBytes {
  manifest: TraceManifest;
  coarse: Uint8Array;
  detail: Uint8Array;
  index: Uint8Array;
}

/**
 * How a decoded trace's bytes were verified — carried **in the type**, not left to be inferred
 * from which function produced the value.
 *
 * There are exactly two decode entry points and they are not interchangeable. {@link openTrace}
 * opens a **published** trace: a manifest declares every component's byte length, record count and
 * BLAKE3 digest, the caller has checked the digests, and every one of `openTrace`'s own gates is a
 * comparison against those declarations. {@link decodeInFlightPrefix} reads a **staged generation**
 * — a trace still being written by this process — which by definition has no manifest and therefore
 * no declared checksum to check (`rounds-trace` design **D-D**: the permission is scoped to the
 * writing process precisely *because* the commit marker is not there yet).
 *
 * Naming the difference is what stops the second path from being mistaken for the first. A
 * published read that fell through to the unverified branch would serve bytes nobody checked while
 * a reader believed the commit marker had been honoured — the plausible-but-false-replay class
 * **D-121** exists for. So the brand is a required literal on both results: neither is assignable
 * where the other is expected, and a function that means "a verified trace" can say so.
 */
export type TraceVerification = 'checksum-verified' | 'in-process-unverified';

/** An opened, validated trace: the coarse tier decoded whole, the index resident, and
 * any detail tick reachable as an O(1) range read (plan A4). */
export interface DecodedTrace {
  /**
   * Always `'checksum-verified'`. Not a runtime check and not a claim this module can make on its
   * own — the digests are the caller's to verify before opening — but a *type-level* statement that
   * this value came off the manifest-declared path, so an {@link InFlightPrefix} can never be
   * handed to something expecting one. See {@link TraceVerification}.
   */
  verification: 'checksum-verified';
  manifest: TraceManifest;
  firstTick: number;
  terminalTick: number;
  coarseCadenceTicks: number;
  coarseSamples: readonly CoarseSample[];
  /** The preamble's run-global state-name table, which every bee's `stateId` indexes. */
  stateNames: readonly string[];
  index: readonly IndexEntry[];
  /** The coarse sample at-or-before `tick` (the tier is whole, so this never misses
   * inside the trace's span). */
  coarseAt(tick: number): CoarseSample | null;
  /** The detail record for exactly `tick`, decoded on demand; `null` if the trace
   * declares no record for it. */
  detailAt(tick: number): DetailRecord | null;
}

function checkComponent(
  name: TraceComponentName,
  bytes: Uint8Array,
  decl: TraceComponentDecl,
  actualRecordCount: number | null,
): void {
  if (bytes.byteLength !== decl.byteLen) {
    throw new TraceCorruptionError(
      name,
      `declared ${decl.byteLen} byte(s), got ${bytes.byteLength}`,
    );
  }
  if (actualRecordCount !== null && actualRecordCount !== decl.recordCount) {
    throw new TraceCorruptionError(
      name,
      `declared ${decl.recordCount} record(s), decoded ${actualRecordCount}`,
    );
  }
}

/**
 * Validates and opens a published trace. Fail-closed by construction: byte lengths and
 * record counts must match the manifest, index ticks must ascend, and every index entry
 * must name a range wholly inside `detail.bin`. Anything else is a
 * `TraceCorruptionError` — in this change absent or inconsistent declared bytes are
 * never intentional (`rounds-trace` D4), so there is no "degrade to coarse" branch here.
 */
export function openTrace(bytes: TraceBytes): DecodedTrace {
  const { manifest } = bytes;

  const { preamble, samples: coarseSamples } = decodeCoarse(bytes.coarse);
  // The manifest's declared record count is the whole coarse tier, preamble included — one
  // more than the sample count, exactly as `waggle-server`'s own reader checks it.
  checkComponent('coarse', bytes.coarse, manifest.components.coarse, coarseSamples.length + 1);

  const index = decodeIndex(bytes.index);
  checkComponent('index', bytes.index, manifest.components.index, index.length);

  checkComponent('detail', bytes.detail, manifest.components.detail, null);

  const byTick = new Map<number, IndexEntry>();
  let previousTick = -1;
  for (const entry of index) {
    if (entry.tick <= previousTick) {
      throw new TraceCorruptionError('index', `tick ${entry.tick} does not follow ${previousTick}`);
    }
    // Tick contiguity is **required** from format version 4 (design **E5**): under a delta
    // format a silently missing tick would corrupt every following tick in its span while
    // every other validation here still passed clean, so a gap inside the written range is
    // refused rather than tolerated the way an out-of-range tick's absence still is.
    if (previousTick !== -1 && entry.tick !== previousTick + 1) {
      throw new TraceCorruptionError(
        'index',
        `tick ${entry.tick} does not follow contiguously from ${previousTick}: a gap inside ` +
          'the trace\'s tick range is corruption from format version 4',
      );
    }
    previousTick = entry.tick;
    const end = entry.offset + entry.length;
    if (entry.length < 4 || end > bytes.detail.byteLength) {
      throw new TraceCorruptionError(
        'index',
        `entry for tick ${entry.tick} spans ${entry.offset}..${end}, outside detail's ${bytes.detail.byteLength} byte(s)`,
      );
    }
    byTick.set(entry.tick, entry);
  }
  if (index.length !== manifest.components.detail.recordCount) {
    throw new TraceCorruptionError(
      'detail',
      `detail declares ${manifest.components.detail.recordCount} record(s) but the index holds ${index.length}`,
    );
  }
  // The manifest's own declared span must agree with the index it is describing. Nothing above
  // checks this: every other gate compares the index against itself or against `detail.bin`, so
  // a manifest whose `firstTick`/`terminalTick` disagree with `index[0]`/`index[last]` would
  // otherwise fail **open** rather than closed — `spanAnchor` is computed from `manifest.firstTick`
  // (design **E1**), so a `firstTick` reported higher than the index's real first tick would move
  // every anchor forward, skip the entries `detailAt` needs, and report a tick the index
  // demonstrably holds as **absent** — a silent wrong answer standing in for a corrupt one.
  if (index.length > 0) {
    const indexFirstTick = index[0]!.tick;
    const indexLastTick = index[index.length - 1]!.tick;
    if (manifest.firstTick !== indexFirstTick) {
      throw new TraceCorruptionError(
        'index',
        `manifest declares firstTick ${manifest.firstTick} but the index's first entry is tick ${indexFirstTick}`,
      );
    }
    if (manifest.terminalTick !== indexLastTick) {
      throw new TraceCorruptionError(
        'index',
        `manifest declares terminalTick ${manifest.terminalTick} but the index's last entry is tick ${indexLastTick}`,
      );
    }
  }

  // The decodable unit from format version 4 is a keyframe-anchored span, not a lone record, so
  // a cache miss replays one whole cadence block (anchor..anchor+cadence-1, clamped to the
  // trace's own range) and caches every tick it produces — not only the one asked for. A scrub
  // that lands repeatedly inside one block after that is a map lookup, exactly as it was when
  // every record decoded on its own.
  const detailCache = new Map<number, DetailRecord>();
  const cadence = Math.max(1, manifest.keyframeCadenceTicks);
  const readDetailBytes = (offset: number, length: number): Uint8Array =>
    bytes.detail.subarray(offset, offset + length);

  return {
    verification: 'checksum-verified',
    manifest,
    firstTick: manifest.firstTick,
    terminalTick: manifest.terminalTick,
    coarseCadenceTicks: manifest.coarseCadenceTicks,
    coarseSamples,
    stateNames: preamble.stateNames,
    index,
    coarseAt(tick: number): CoarseSample | null {
      let found: CoarseSample | null = null;
      for (const sample of coarseSamples) {
        if (sample.tick <= tick) {
          found = sample;
        } else {
          break;
        }
      }
      return found;
    },
    detailAt(tick: number): DetailRecord | null {
      const cached = detailCache.get(tick);
      if (cached !== undefined) {
        return cached;
      }
      if (!byTick.has(tick)) {
        return null;
      }
      const anchor = spanAnchor(tick, manifest.firstTick, cadence);
      const blockEnd = Math.min(manifest.terminalTick, anchor + cadence - 1);
      const records = replayDetailSpan(
        index,
        readDetailBytes,
        preamble,
        manifest.firstTick,
        cadence,
        anchor,
        blockEnd,
      );
      for (const record of records) {
        detailCache.set(record.tick, record);
      }
      return detailCache.get(tick) ?? null;
    },
  };
}

// ---------------------------------------------------------------------------
// Reading a trace that is still being written
// ---------------------------------------------------------------------------

/** A staged generation's component bytes, as much of each as has been written so far. */
export interface InFlightBytes {
  coarse: Uint8Array;
  detail: Uint8Array;
  index: Uint8Array;
}

/**
 * The portion of a trace that has been written, decoded — the writer's own view of its own
 * still-growing file.
 *
 * Deliberately **not** a {@link DecodedTrace}, and not merely because it lacks a manifest. Three
 * things are genuinely different about it, and collapsing them into one type would hide each:
 *
 *   * `verification` is `'in-process-unverified'`. There is no declared digest to check.
 *   * The span is **open at the top**: {@link writeHeadTick} moves as the run steps, and both it and
 *     {@link firstTick} are `null` before the first record lands. A `terminalTick` would be a
 *     number this value does not have.
 *   * Nothing here is a *published* component. There is no `manifest` and no `index` field to be
 *     mistaken for one, and there are exactly **two** ways to obtain this value —
 *     {@link decodeInFlightPrefix} from a staged generation's bytes, and
 *     {@link inFlightPrefixFromSnapshot} from a decoded {@link InFlightSnapshot} that crossed a
 *     thread inside the writing page — both named so that a reader of the call site knows which
 *     path it took, and neither able to produce a {@link DecodedTrace}.
 */
export interface InFlightPrefix {
  /** See {@link TraceVerification}. Always `'in-process-unverified'`. */
  verification: 'in-process-unverified';
  /** Every tick a record is readable for right now, ascending. */
  ticks: readonly number[];
  /** The coarse samples flushed so far, in tick order. Exposed for the same reason
   * {@link DecodedTrace.coarseSamples} is: a caller that must carry the prefix somewhere (see
   * {@link inFlightSnapshotOf}) cannot reconstruct the tier from {@link InFlightPrefix.coarseAt}
   * alone, because the cadence is the writer's and not the reader's to guess. */
  coarseSamples: readonly CoarseSample[];
  /** The preamble's state-name table, or empty before the preamble frame is whole. Carried for the
   * same reason {@link DecodedTrace.stateNames} is: a bee's `stateId` is meaningless without it,
   * and the watch surface renders through the same adapter the published path does. */
  stateNames: readonly string[];
  /** The first readable tick, or `null` before any record has been written. */
  firstTick: number | null;
  /** The **write head**: the highest tick a whole record has been written for, or `null`. This is
   * the inclusive bound a chasing playhead may reach. */
  writeHeadTick: number | null;
  /** The coarse sample at-or-before `tick`, from the samples flushed so far. */
  coarseAt(tick: number): CoarseSample | null;
  /** The detail record for exactly `tick`, or `null` when the writer has not reached it. **Absent
   * is a normal result** — it is what "the run has not simulated that moment yet" looks like. */
  detailAt(tick: number): DetailRecord | null;
  /**
   * How many detail records this instance has actually decoded so far — construction's own
   * write-head check plus every {@link detailAt} call since, **not** double-counting a tick
   * resolved from cache. Mirrors why the Rust reader exposes `read_stats()`: a bound on
   * replay cost is a claim worth measuring, not just asserting. A watch poll's whole cost is
   * this value read once after the poll's calls are done, and it is bounded by one keyframe
   * cadence plus the ticks actually requested — never by how long the run has been going.
   */
  readonly recordsReplayed: number;
}

/**
 * Decodes as much of a staged generation as is whole, and stops there.
 *
 * ## Why this is tolerant where {@link openTrace} is fail-closed
 *
 * `openTrace` refuses a component whose bytes disagree with its manifest, because on a *published*
 * trace a disagreement can only be damage. Mid-run there is no manifest to disagree with, and a
 * half-written trailing record is not damage at all — it is the normal state of a file being
 * appended to, observed between two appends. Refusing the whole prefix for it would make the world
 * view flicker off on the tick boundaries it is supposed to be following.
 *
 * So the rule here is **truncate, never throw**: the readable prefix ends at the last record that
 * is wholly present and consistent, and everything after that is simply not readable yet. Every
 * bound `openTrace` checks against the manifest is still checked here against the *bytes* —
 *
 *   * index entries are read only in whole {@link INDEX_ENTRY_BYTES} units;
 *   * an entry whose `offset + length` reaches past the detail bytes present ends the prefix;
 *   * a non-ascending tick, a frame whose width disagrees with its entry, or a record carrying a
 *     tick other than the one the index named ends the prefix.
 *
 * — so a caller can never be handed a record assembled out of bytes that do not fit together. What
 * it cannot check is the digest, which is exactly what the brand on the result records.
 *
 * The permission this serves is narrow by design (`rounds-trace` design **D-D**, and
 * `rounds-local-trace-store`'s "the review surface MAY read the portion already written **while the
 * run is still stepping**"): it is granted to the writing process over bytes it wrote itself. It is
 * not a read protocol, nothing here is durable, and the caller obtaining these bytes is the caller
 * that produced them.
 */
export function decodeInFlightPrefix(bytes: InFlightBytes): InFlightPrefix {
  // Whole entries only. A partial trailing entry is the writer mid-append, not corruption. This
  // pass is metadata-only — no record content is read — so it costs O(entries), never
  // O(entries × record size): the expensive part, replaying content, is deferred to
  // `decodeThroughSpan` below and paid only for ticks someone actually asks for.
  const wholeIndexBytes =
    Math.floor(bytes.index.byteLength / INDEX_ENTRY_BYTES) * INDEX_ENTRY_BYTES;
  const entries = decodeIndex(bytes.index.subarray(0, wholeIndexBytes));
  const readable: IndexEntry[] = [];
  {
    let previousTick = -1;
    for (const entry of entries) {
      if (entry.tick <= previousTick) break;
      if (previousTick !== -1 && entry.tick !== previousTick + 1) break;
      if (entry.length < 4) break;
      if (entry.offset + entry.length > bytes.detail.byteLength) break;
      previousTick = entry.tick;
      readable.push(entry);
    }
  }

  // The coarse tier, up to the last whole frame. Decoded through the shipped `decodeCoarse` over a
  // complete-frames-only slice rather than a second, laxer sample decoder — so an in-flight coarse
  // sample and a published one can never be decoded differently.
  let coarseWhole = 0;
  for (;;) {
    const framed = unframe(bytes.coarse.subarray(coarseWhole));
    if (framed === null) break;
    coarseWhole += framed.framedLength;
  }
  const coarseBytes = bytes.coarse.subarray(0, coarseWhole);
  // No whole frame yet is the ordinary "nothing written" state, not a truncation to tolerate: the
  // writer emits the preamble before its first detail record (design **E6**), so if the detail
  // replay below ever turns out non-empty there is necessarily a whole preamble frame here too.
  // `decodeCoarse` still throws on a genuinely malformed whole frame — same as it would for a
  // published trace — because a *complete* record that will not decode is damage, not a tear.
  let preamble: CoarsePreamble | null = null;
  let coarseSamples: CoarseSample[] = [];
  if (coarseBytes.byteLength > 0) {
    const decoded = decodeCoarse(coarseBytes);
    preamble = decoded.preamble;
    coarseSamples = decoded.samples;
  }

  const detailCache = new Map<number, DetailRecord>();
  // See `InFlightPrefix.recordsReplayed`: incremented exactly once per record actually decoded
  // through `decodeOne` below, never for a cache hit, so a test can measure the bound directly
  // instead of asserting it by reading the algorithm.
  let recordsReplayed = 0;

  /**
   * Decodes `tick`'s whole span and caches every record replayed along the way, bounded to at
   * most one keyframe cadence of work **regardless of how much history precedes it** — the fix
   * for implementation-review's finding that the old sequential-from-tick-0 replay cost grew
   * with the whole run (≈13,950 record replays over a 900-tick watch window where ~900 sufficed).
   *
   * Two passes, neither of which needs the manifest's cadence (this value has no manifest at
   * all): a **backward scan** for either the governing keyframe or the nearest entry this same
   * `InFlightPrefix` has already decoded — whichever comes first — peeking only the one-byte
   * section mask that leads every record to classify a keyframe, never a full decode just to
   * classify one; followed by a **forward replay** from there through to `tick`, calling
   * `decodeOne` directly rather than going through {@link SpanReplayer} so it can resume from an
   * already-cached record instead of the anchor every time. This is what keeps a run of
   * ascending `detailAt` calls in one poll — exactly what {@link inFlightSnapshotOf} makes —
   * linear in the ticks requested rather than quadratic: each call after the first resumes from
   * its immediate predecessor, already in `detailCache`, instead of replaying the whole span
   * again. The backward scan itself is bounded by construction: the writer's very first record
   * is unconditionally a keyframe (`DetailEncoder::is_keyframe_tick` with no `first_tick`
   * recorded yet), so index 0 always terminates it if nothing closer is cached.
   *
   * Never throws: a replay failure reads as "not decodable yet", the same as a torn frame —
   * this function is the one place that promise has to hold, since every consumer below it
   * relies on it.
   */
  function decodeThroughSpan(tick: number): DetailRecord | null {
    const cached = detailCache.get(tick);
    if (cached !== undefined) return cached;
    if (preamble === null) return null;
    const targetIndex = readable.findIndex((entry) => entry.tick === tick);
    if (targetIndex === -1) return null;

    let startIndex = targetIndex;
    while (
      startIndex > 0 &&
      !detailCache.has(readable[startIndex]!.tick) &&
      bytes.detail[readable[startIndex]!.offset + 4] !== ALL_SECTIONS_KEYFRAMED
    ) {
      startIndex -= 1;
    }
    let base: DetailRecord | null = null;
    const resumeTick = readable[startIndex]!.tick;
    if (detailCache.has(resumeTick)) {
      base = detailCache.get(resumeTick)!;
      startIndex += 1; // that entry is already resolved; begin decoding from the next one
    }
    // Otherwise `readable[startIndex]` is the governing keyframe (or index 0, which is always
    // one) — `base` stays `null` and it decodes fresh below.

    for (let i = startIndex; i <= targetIndex; i++) {
      const entry = readable[i]!;
      const framed = unframe(bytes.detail.subarray(entry.offset, entry.offset + entry.length));
      // `readable` already bounds-checked every entry, so a torn frame here would mean the bytes
      // changed under us mid-decode — treated the same as "not there yet", never thrown.
      if (framed === null || framed.framedLength !== entry.length) return null;
      let record: DetailRecord;
      try {
        record = decodeOne(framed.payload, preamble, base);
      } catch {
        return null;
      }
      if (record.tick !== entry.tick) return null;
      detailCache.set(entry.tick, record);
      recordsReplayed += 1;
      base = record;
    }
    return detailCache.get(tick) ?? null;
  }

  // Design **E6**: the prefix ends at the last tick whose **whole span** is present — the anchor
  // keyframe plus every delta up to it — not merely the last whole *record*. `readable`'s own
  // metadata checks already establish that for every tick but the last (a torn or gapped tail is
  // exactly what they catch), so only the write head itself is actually replayed here to confirm
  // it — bounded to one cadence block, and popped back one tick at a time in the unreachable case
  // it does not decode (the very first tick can never fail this way, since it is always a
  // keyframe by construction, so the loop is bounded and always terminates with an answer).
  let ticks: number[] = readable.map((entry) => entry.tick);
  while (ticks.length > 0 && decodeThroughSpan(ticks[ticks.length - 1]!) === null) {
    ticks.pop();
  }
  const ticksPresent = new Set(ticks);

  return {
    verification: 'in-process-unverified',
    ticks,
    coarseSamples,
    stateNames: preamble?.stateNames ?? [],
    firstTick: ticks.length === 0 ? null : ticks[0]!,
    writeHeadTick: ticks.length === 0 ? null : ticks[ticks.length - 1]!,
    coarseAt(tick: number): CoarseSample | null {
      let found: CoarseSample | null = null;
      for (const sample of coarseSamples) {
        if (sample.tick <= tick) {
          found = sample;
        } else {
          break;
        }
      }
      return found;
    },
    detailAt(tick: number): DetailRecord | null {
      // Bounded to `ticksPresent` — the write-head-verified set — rather than to whatever
      // `readable` alone would allow, so a tick past the confirmed write head never triggers a
      // decode attempt bytes have not settled around yet.
      if (!ticksPresent.has(tick)) return null;
      return decodeThroughSpan(tick);
    },
    get recordsReplayed(): number {
      return recordsReplayed;
    },
  };
}

// ---------------------------------------------------------------------------
// Carrying a readable prefix between the writer's thread and the drawing thread
// ---------------------------------------------------------------------------

/**
 * A **decoded** in-flight prefix, as plain data — the only form in which a readable extent may cross
 * a thread boundary inside the writing page (`rounds-practice-sandbox` task 5.13).
 *
 * ## Why this exists at all, and why it carries records rather than bytes
 *
 * The writer of a device-side practice run is a `Worker` (OPFS sync access handles are worker-only)
 * and the review surface is the main thread's canvas, so "the review surface MAY read the portion
 * already written" is only exercisable if *something* crosses. What crosses is this: **world state
 * already decoded, for rendering**, with no manifest, no component bytes, no store handle and no
 * checksum claim.
 *
 * Records rather than bytes is a deliberate narrowing rather than an efficiency choice. `rounds-trace`
 * forbids the in-flight extent being "exported, or treated as durable", and `LocalGeneration` honours
 * that by exposing the extent as a **count**, never as bytes — "a caller learns how far it may read
 * and cannot obtain, export or persist a component through it". A byte channel out of the worker
 * would hand the drawing thread exactly the component the store deliberately withholds, and a
 * component's bytes are the one thing that could be re-presented as a trace. Decoded records cannot:
 * there is no manifest to publish them under and no path from here back to {@link openTrace}.
 *
 * It is **not durable** in the only sense available to it — it is a message and then a value in
 * memory, written to no store by anything that consumes it, so a reload leaves nothing behind on
 * either thread.
 */
export interface InFlightSnapshot {
  /** Decoded detail records, ascending by tick. A *delta* in the practice worker's protocol: only
   * the ticks the receiver has not been sent yet. */
  readonly records: readonly DetailRecord[];
  /** Every coarse sample flushed so far, in tick order — sent whole rather than as a delta, because
   * the tier is small and `coarseAt` answers over the whole of it. */
  readonly coarseSamples: readonly CoarseSample[];
  /** The run's state-name table, from the preamble the writer emits before its first detail record.
   * Match-static, so it crosses once per message unchanged rather than being diffed. */
  readonly stateNames: readonly string[];
}

/**
 * Copies out the part of a prefix that has not been reported yet, as plain data.
 *
 * `afterTick` is **exclusive**: pass the highest tick already sent, or `null` for the whole readable
 * prefix. Decoding each record here rather than on the far side is what makes the payload bounded and
 * self-describing — the receiver never holds an index, an offset, or anything it could resolve a
 * second read against.
 */
export function inFlightSnapshotOf(
  prefix: InFlightPrefix,
  afterTick: number | null = null,
): InFlightSnapshot {
  const records: DetailRecord[] = [];
  for (const tick of prefix.ticks) {
    if (afterTick !== null && tick <= afterTick) continue;
    const record = prefix.detailAt(tick);
    // A tick the prefix lists but cannot decode is simply not readable yet — the same "not yet"
    // `decodeInFlightPrefix` reports by truncating, rather than a failure to propagate.
    if (record !== null) records.push(record);
  }
  return {
    records,
    coarseSamples: [...prefix.coarseSamples],
    stateNames: [...prefix.stateNames],
  };
}

/**
 * Rebuilds a prefix from a snapshot, **with the unverified brand intact**.
 *
 * The write head is the highest tick this value actually holds a record for, not a number it was
 * told: a prefix whose `writeHeadTick` ran ahead of its own records would report a bound a chasing
 * playhead could reach and find nothing at, which is the "plausible but false" shape in miniature.
 * How far the *run* has written is a separate signal with a separate owner (the run's progress
 * report, which the scrub track's written extent reads).
 */
export function inFlightPrefixFromSnapshot(snapshot: InFlightSnapshot): InFlightPrefix {
  const byTick = new Map<number, DetailRecord>();
  for (const record of snapshot.records) {
    byTick.set(record.tick, record);
  }
  const ticks = [...byTick.keys()].sort((a, b) => a - b);
  const coarseSamples = [...snapshot.coarseSamples].sort((a, b) => a.tick - b.tick);
  return {
    verification: 'in-process-unverified',
    ticks,
    coarseSamples,
    stateNames: snapshot.stateNames,
    firstTick: ticks.length === 0 ? null : ticks[0]!,
    writeHeadTick: ticks.length === 0 ? null : ticks[ticks.length - 1]!,
    coarseAt(tick: number): CoarseSample | null {
      let found: CoarseSample | null = null;
      for (const sample of coarseSamples) {
        if (sample.tick <= tick) {
          found = sample;
        } else {
          break;
        }
      }
      return found;
    },
    detailAt(tick: number): DetailRecord | null {
      return byTick.get(tick) ?? null;
    },
    // Always 0: this side never decodes anything. The replay already happened once, in the
    // writer's own `decodeInFlightPrefix`, before the records crossed as an `InFlightSnapshot` —
    // this is a lookup over what arrived, not a second decode of it.
    recordsReplayed: 0,
  };
}
